/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getTrackingTraits } from '@/services/daily/complete-tasks.service';
import { getPage } from '@/services/profile/profile.service';
import type { ProfilePage } from '@edwin-group/people-graphql';
import { useAuthentication } from '@/hooks/useAuthentication';

export function useTraitCalendar() {
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
  const [page, setPage] = useState<ProfilePage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [days, setDays] = useState<
    {
      day: number;
      emoji: string;
      label: string;
      isDisabledTracking: boolean;
      isCurrentMonth: boolean;
    }[]
  >([]);
  const { toggleOnboardingStatus } = useAuthentication();

  // Compute Monday-started week indices
  const startDay = (currentMonth.startOf('month').day() + 6) % 7;
  const endDay = (currentMonth.endOf('month').day() + 6) % 7;

  const daysInMonth = currentMonth.daysInMonth();

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, 'month'));
  };

  const handleMonthData = (response: { data: any; error: string | undefined }) => {
    const previousMonth = currentMonth.clone().subtract(1, 'month');
    const nextMonth = currentMonth.clone().add(1, 'month');
    const prevMonthDaysCount = previousMonth.daysInMonth();
    const nextMonthStartDay = 1;
    const daysPerMonth: Array<any> = [];

    // Helper to find the trait data for a given date
    const findDailyTrait = (targetMonth: dayjs.Dayjs, dayIndex: number) => {
      return response.data.find((record: { day: string | number | dayjs.Dayjs | Date | null | undefined }) =>
        dayjs(record.day).isSame(targetMonth.startOf('month').add(dayIndex, 'day'), 'day'),
      );
    };

    // Helper to create day entry object
    const createDayEntry = ({
      dayNumber,
      targetMonth,
      dailyTrait,
      isCurrentMonth,
    }: {
      dayNumber: number;
      targetMonth: dayjs.Dayjs;
      dailyTrait?: any;
      isCurrentMonth: boolean;
    }) => {
      if (!dailyTrait) {
        // No trait data, use defaults
        return {
          day: dayNumber,
          dayComplete: `${dayNumber}/${targetMonth.format('MM')}/${targetMonth.format('YYYY')}`,
          emoji: '',
          label: '',
          isDisabledTracking: false,
          isCurrentMonth,
        };
      }

      const trait = JSON.parse(dailyTrait.focusArea);

      return {
        day: dayNumber,
        dayComplete: `${dayNumber}/${targetMonth.format('MM')}/${targetMonth.format('YYYY')}`,
        emoji: trait?.activeLeadershipWheel?.attributes?.overallMoodIcon?.data?.attributes?.url || '',
        label: trait?.activeLeadershipWheel?.attributes?.focusArea || '',
        description: trait?.activeLeadershipWheel?.attributes?.description || '',
        advice: trait?.activeLeadershipWheel?.attributes?.advice,
        isDisabledTracking: trait?.isDisabledTracking || false,
        isCurrentMonth,
      };
    };

    // Fill in days from previous month
    // We use startDay to know how many days from the previous month to show
    for (let i = startDay - 1; i >= 0; i--) {
      const dayNumber = prevMonthDaysCount - i;
      const dailyTrait = findDailyTrait(previousMonth, dayNumber - 1);
      // Note: If dayNumber is 30 and month starts from index 0, we use dayNumber - 1 for zero-based indexing

      daysPerMonth.push(
        createDayEntry({
          dayNumber,
          targetMonth: previousMonth,
          dailyTrait,
          isCurrentMonth: false,
        }),
      );
    }

    // Fill in days for the current month
    for (let i = 1; i <= daysInMonth; i++) {
      const dailyTrait = findDailyTrait(currentMonth, i - 1);
      daysPerMonth.push(
        createDayEntry({
          dayNumber: i,
          targetMonth: currentMonth,
          dailyTrait,
          isCurrentMonth: true,
        }),
      );
    }

    // Fill in days from the next month
    // 6 - endDay represents how many days from the next month to fill in
    for (let i = 0; i < 6 - endDay; i++) {
      const dayNumber = nextMonthStartDay + i;
      const dailyTrait = findDailyTrait(nextMonth, dayNumber - 1);

      daysPerMonth.push(
        createDayEntry({
          dayNumber,
          targetMonth: nextMonth,
          dailyTrait,
          isCurrentMonth: false,
        }),
      );
    }

    setDays(daysPerMonth);
  };

  const onChangeTraitTrack = async () => {
    setIsLoading(true);
    toggleOnboardingStatus('onboardLeadershipWheelCompleted');
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      const firstDay = currentMonth.clone().subtract(1, 'month').startOf('month').toDate();
      const lastDay = currentMonth.clone().add(1, 'month').endOf('month').toDate();

      // Always fetch tracking traits when currentMonth changes
      const trackingTraits = await getTrackingTraits(firstDay, lastDay);

      if (!trackingTraits) {
        return null;
      }
      handleMonthData(trackingTraits);

      // Only fetch page data if it's not already set (i.e., on initial mount)
      if (!page) {
        const pageData = await getPage();

        setPage(pageData.page);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, page]);

  return {
    currentMonth,
    days,
    handlePrevMonth,
    handleNextMonth,
    page,
    onChangeTraitTrack,
    isLoading,
  };
}
